import {
  helpWidgetDragWidgetNewSrc4Evid1508,
  helpWidgetLoadWidgetInBackgroundNewSrc4Evid1501,
  helpWidgetWidgetCloseNewSrc4Evid1503,
  helpWidgetWidgetCloseNewSrc4Evid1511,
} from '@wix/bi-logger-customer-care-data/v2';
import webBiLogger from '@wix/web-bi-logger';
import { WidgetClosingTrigger } from '@wix/wix-chatbot-common/bi';
import { getBiAreaAndPlatform } from '@wix/wix-chatbot-common/browser';

import { getState } from '../store';
import { getContext } from '../utils/context';
import { getCurrentPage, pageEnumToString } from '../utils/page';
import { widgetModel } from '../widgetModel';

const logger = webBiLogger.factory().logger();

// Common BI context interface
interface BiContext {
  context_area: string;
  context_platform: string;
  context_url: string;
  target_account_id?: string;
}

// Helper to get common BI context
const getBiContext = (): BiContext => {
  const url = new URL(window.location.href);
  const { contextArea, contextPlatform } = getBiAreaAndPlatform(url.href);
  const {
    context: { user },
  } = getState();

  return {
    context_area: contextArea,
    context_platform: contextPlatform,
    context_url: `${url.origin}${url.pathname}`, // URL without query params
    target_account_id: user?.accountId,
  };
};

// Helper to get widget language with fallback
const getWidgetLanguage = (): string => {
  const {
    chatbot: { conversation },
  } = getState();

  // Try conversation language first, then fallback to widget model locale
  return conversation.lang || widgetModel.getLocale() || '';
};

// Event specific logging functions
export const logHelpWidgetLoadInBackground = async () => {
  const biContext = getBiContext();
  const context = await getContext();
  const {
    chatbot: { conversation },
  } = getState();

  const { metaSiteId } = context;

  logger.report(
    helpWidgetLoadWidgetInBackgroundNewSrc4Evid1501({
      context_msid: metaSiteId,
      notification_count: conversation.unreadCount || 0,
      ...biContext,
    }),
  );
};

// BI event for widget open - evid 1503
export const logHelpWidgetWidgetOpen = async (forceWidgetSessionId?: string, requestId?: string) => {
  const biContext = getBiContext();
  const context = await getContext();
  const {
    chatbot: { conversation, chatbotActiveSession },
    widgetSessionId,
    bi: { placement },
  } = getState();

  const { metaSiteId } = context;

  let conversationId = conversation.conversationId;
  if (!conversationId && chatbotActiveSession?.conversationId) {
    conversationId = chatbotActiveSession.conversationId;
  }

  logger.report(
    helpWidgetWidgetCloseNewSrc4Evid1503({
      chatbot_conversation_id: conversationId || undefined,
      context_msid: metaSiteId,
      open_source: placement || undefined,
      self_service_session_id: forceWidgetSessionId ?? widgetSessionId,
      widget_language: getWidgetLanguage(),
      widget_page_type: pageEnumToString(getCurrentPage()),
      server_request_id: requestId,
      ...biContext,
    }),
  );
};

// BI event for widget close - evid 1511
export const logHelpWidgetWidgetClose = async (closingTrigger: WidgetClosingTrigger) => {
  const biContext = getBiContext();
  const context = await getContext();
  const {
    chatbot: { conversation, chatbotActiveSession },
    widgetSessionId,
  } = getState();

  const { metaSiteId } = context;

  let conversationId = conversation.conversationId;
  if (!conversationId && chatbotActiveSession?.conversationId) {
    conversationId = chatbotActiveSession.conversationId;
  }

  logger.report(
    helpWidgetWidgetCloseNewSrc4Evid1511({
      chatbot_conversation_id: conversationId || undefined,
      closing_trigger: closingTrigger,
      context_msid: metaSiteId,
      self_service_session_id: widgetSessionId,
      widget_language: getWidgetLanguage(),
      widget_page_type: pageEnumToString(getCurrentPage()),
      ...biContext,
    }),
  );
};

export const logHelpWidgetDragWidget = async () => {
  const biContext = getBiContext();
  const context = await getContext();
  const {
    chatbot: { conversation },
    widgetSessionId,
  } = getState();

  logger.report(
    helpWidgetDragWidgetNewSrc4Evid1508({
      ...biContext,
      context_msid: context.metaSiteId,
      self_service_session_id: widgetSessionId,
      chatbot_conversation_id: conversation.conversationId,
      widget_language: getWidgetLanguage(),
      widget_page_type: pageEnumToString(getCurrentPage()),
    }),
  );
};
